"use client"

import { useState } from "react"
import { useWishlist } from "@/lib/wishlist-context"
import { useCart } from "@/lib/cart-context"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ConfirmationDialog } from "@/components/confirmation-dialog"
import { Heart, ShoppingCart, Trash2, Package } from "lucide-react"
import { toast } from "sonner"
import Link from "next/link"
import Image from "next/image"
import { buildFullImageUrl } from "@/lib/utils"

export default function WishlistPage() {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000"
    const { wishlist, removeFromWishlist, clearWishlist, wishlistCount } = useWishlist()
    const { addToCart } = useCart()
    const [showClearConfirm, setShowClearConfirm] = useState(false)
    const [itemToRemove, setItemToRemove] = useState<{ id: string; name: string } | null>(null)

    const handleAddToCart = (product: any) => {
        if (!product._id || product.stock === 0) {
            toast.error("Produit indisponible")
            return
        }

        addToCart(product, 1, product.price, null)
        toast.success(`${product.name} ajouté au panier`)
    }

    const handleRemove = (productId: string | undefined, productName: string) => {
        if (!productId) return
        setItemToRemove({ id: productId, name: productName })
    }

    const confirmRemove = () => {
        if (!itemToRemove) return
        removeFromWishlist(itemToRemove.id)
        toast.success(`${itemToRemove.name} retiré de votre panier`)
        setItemToRemove(null)
    }

    const handleClearAll = () => {
        setShowClearConfirm(true)
    }

    const confirmClearAll = () => {
        clearWishlist()
        toast.success("Votre panier a été vidé")
        setShowClearConfirm(false)
    }

    return (
        <div className="flex min-h-screen flex-col">
            <Navbar />
            <main className="flex-1 bg-gradient-to-b from-primary/5 to-background">
                <div className="container mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="mb-8 flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground">Mon Panier</h1>
                            <p className="mt-2 text-muted-foreground">
                                {wishlistCount} {wishlistCount > 1 ? "produits" : "produit"} dans votre liste
                            </p>
                        </div>
                        {wishlistCount > 0 && (
                            <Button variant="outline" onClick={handleClearAll} className="gap-2">
                                <Trash2 className="h-4 w-4" />
                                Vider la liste
                            </Button>
                        )}
                    </div>

                    {/* Empty State */}
                    {wishlistCount === 0 ? (
                        <Card className="border-dashed">
                            <CardContent className="flex flex-col items-center justify-center py-16">
                                <div className="rounded-full bg-primary/10 p-6 mb-4">
                                    <Heart className="h-12 w-12 text-primary" />
                                </div>
                                <h2 className="text-2xl font-semibold mb-2">Votre panier est vide</h2>
                                <p className="text-muted-foreground mb-6 text-center max-w-md">
                                    Parcourez nos produits et cliquez sur le cœur pour ajouter vos favoris à votre panier. Ils apparaîtront ici pour un accès rapide lors de votre prochaine visite.
                                </p>
                                <Button asChild>
                                    <Link href="/boutique" className="gap-2">
                                        <Package className="h-4 w-4" />
                                        Découvrir nos produits
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        /* Products Grid */
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {wishlist.map((product) => (
                                <Card key={product._id} className="group relative overflow-hidden transition-all hover:shadow-lg">
                                    {/* Remove Button */}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-2 top-2 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm hover:bg-destructive hover:text-destructive-foreground"
                                        onClick={() => handleRemove(product._id, product.name)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>

                                    <CardContent className="p-4">
                                        {/* Product Image */}
                                        <div className="relative mb-4 aspect-square overflow-hidden rounded-lg bg-muted">
                                            {(() => {
                                                const imageUrl = buildFullImageUrl(product.images?.[0])
                                                return imageUrl ? (
                                                    <Image
                                                        src={imageUrl}
                                                        alt={product.name}
                                                        fill
                                                        unoptimized
                                                        className="object-cover transition-transform group-hover:scale-105"
                                                    />
                                                ) : (
                                                    <div className="flex h-full items-center justify-center">
                                                        <Package className="h-16 w-16 text-muted-foreground/50" />
                                                    </div>
                                                )
                                            })()}

                                            {/* Stock Badge */}
                                            {product.stock === 0 && product.stock !== null && product.stock !== undefined && (
                                                <Badge className="absolute right-2 bottom-2 bg-gray-500">
                                                    Rupture de stock
                                                </Badge>
                                            )}
                                        </div>

                                        {/* Product Info */}
                                        <div className="space-y-2">
                                            <h3 className="font-semibold line-clamp-2 min-h-[2.5rem]">{product.name}</h3>

                                            <div className="flex items-baseline gap-2">
                                                <span className="text-xl font-bold text-primary">
                                                    {product.price.toFixed(0)} FCFA
                                                </span>
                                            </div>

                                            {product.stock > 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    {product.stock} en stock
                                                </p>
                                            )}

                                            {/* Actions */}
                                            <div className="flex gap-2 pt-2">
                                                <Button
                                                    className="flex-1 gap-2"
                                                    onClick={() => handleAddToCart(product)}
                                                    disabled={product.stock === 0}
                                                >
                                                    <ShoppingCart className="h-4 w-4" />
                                                    Ajouter au panier
                                                </Button>
                                                <Button variant="outline" asChild>
                                                    <Link href={`/produits/${product._id}`}>
                                                        Voir
                                                    </Link>
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </main>
            <Footer />

            {/* Confirmation Dialogs */}
            <ConfirmationDialog
                open={showClearConfirm}
                title="Vider le panier"
                description="Êtes-vous sûr de vouloir vider votre panier ? Cette action est irréversible."
                onConfirm={confirmClearAll}
                onCancel={() => setShowClearConfirm(false)}
                confirmText="Vider le panier"
                cancelText="Annuler"
                isDangerous
            />

            <ConfirmationDialog
                open={!!itemToRemove}
                title="Retirer du panier"
                description={`Êtes-vous sûr de vouloir retirer "${itemToRemove?.name}" de votre panier ?`}
                onConfirm={confirmRemove}
                onCancel={() => setItemToRemove(null)}
                confirmText="Retirer"
                cancelText="Annuler"
                isDangerous
            />
        </div>
    )
}
